import PropTypes from 'prop-types';
import { config } from 'config';
import { documentTypeOptions } from 'shared/fixtures';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { professionalActions } from 'core/Professional';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { validations } from 'shared/validations';
import {
    Avatar,
    ButtonsContainer,
    DropContainer,
    ImagePreview,
    InputHidden,
    ProfileContainer,
    ProfileIcon,
    ProfileText,
    ResponsiveCol,
    StyledRow
} from './styled';
import {
    Checkbox,
    Container,
    DateSelect,
    ExpandButton,
    FormGroup,
    Input,
    Loader,
    MaterialIcon,
    SelectSearch,
    TagList,
    Text
} from 'mol-ui';
import {
    CityPicker,
    CompanyPicker,
    CoveragePicker,
    DistrictPicker,
    EnrollmentTypePicker,
    GenderPicker,
    PolyOfficePicker,
    ProvincePicker,
    SpecialtyPicker,
    WorkplacePicker
} from 'views/pickers';
import {
    Col,
    Row
} from 'reactstrap';
import {
    ConfirmationModal,
    ErrorMessage,
    ErrorModal,
    FormLayout,
    NotificationModal,
    PasswordSecurityCheck,
    PhoneInput
} from 'views/components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adminLockedActions, configActions } from './fixtures';
import {
    checkPasswordSecurity,
    clearObject,
    dateToObject,
    objectToDate,
    parseSelect,
    validationSchema
} from 'shared/utils';
import {
    useDispatch,
    useErrors,
    useObjectState,
    useQuery,
    useSelector
} from 'hooks';
import {
    useLocation,
    useParams
} from 'react-router-dom';

const {
    allowChangeRateFirst,
    company: companyConfig,
    doctorsRelated,
    enrollmentSpecialty,
    externalService,
    identificationFreeLength,
    patientIdentificationAlpha,
    polyOffices: polyOfficesConfig,
    professionalAllowExternal,
    professionalBio,
    professionalCallRate,
    professionalCoverageSelection,
    professionalLocation,
    professionalLockedActions,
    recipes,
    ssoLogin,
    turns,
    workplaces: workplacesConfig
} = config;

const DEFAULT_PROFILE = {
    admin_locked_actions: [{ name: 'diagnosis_audits' }],
    all_companies: 0,
    all_coverages: 1,
    all_workplaces: 0,
    appointment_rate: '',
    avatar: '',
    bio: '',
    birth_date: null,
    call_rate: '',
    cellphone_number: '',
    city_id: null,
    companies: [],
    confirm_password: '',
    coverages: [],
    doctor_id: null,
    email: '',
    enrollments: [],
    external: 0,
    external_reference: '',
    external_service: 0,
    first_call_rate: '',
    first_name: '',
    gender: 'Female',
    identification: '',
    identification_id: 1,
    last_name: '',
    locked_actions: [],
    password: '',
    polyoffices: [],
    presential_rate: 0,
    province_id: null,
    receive_appointments: 1,
    receive_interconsults_calls: 0,
    receive_patients_calls: 0,
    specialties: [],
    user_id: null,
    workplaces: []
};

const AddButton = ({
    disabled,
    onClick
}) => (
    <ExpandButton
        caption="Agregar"
        disabled={ disabled }
        icon="add"
        margin="20px 0px 0px 10px"
        size={ 80 }
        width={ 100 }
        onClick={ onClick }
    />
);

AddButton.propTypes = {
    disabled: PropTypes.bool,
    onClick: PropTypes.func
};

const ProfessionalRegistration = () => {

    const dispatch = useDispatch();
    const {
        configurations: {
            integrations,
            strong_password_required
        }
    } = useSelector('config');
    const strongPasswordRequired = strong_password_required === '1';
    const showEnrollmentIntegration = integrations?.includes('OSPEPRI');
    const {
        notificationResponse,
        loading: loadingProfessional,
        registrationError,
        registrationErrorModal,
        saveLoading,
        selected: selectedProfile
    } = useSelector('professional');
    const { pathname } = useLocation();
    const { from } = useQuery();
    const { id: paramId } = useParams();
    const { t } = useTranslation();

    const avatar = useRef(null);
    const [doctorId, setDoctorId] = useState(null);
    const [file, setFile] = useState(null);
    const { clearError, clearError: clearStateError, clearErrors, errors: stateErrors, setErrors: setStateErrors } = useErrors();
    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState('ver');
    const [profile, setProfile, setProfileValue] = useObjectState({ ...DEFAULT_PROFILE });
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedCoverage, setSelectedCoverage] = useState(null);
    const [selectedEnrollment, setSelectedEnrollment, setSelectedEnrollmentValue, setSelectedEnrollmentValues] = useObjectState({});
    const [selectedPolyOffice, setSelectedPolyOffice] = useState(null);
    const [selectedSpecialty, setSelectedSpecialty] = useState(null);
    const [selectedWorkplace, setSelectedWorkplace] = useState(null);
    const [sign, setSign] = useState(null);
    const [stamp, setStamp] = useState(null);

    const editMode = mode === t('editar');
    const viewMode = mode === t('ver');

    const onSignDrop = useCallback(acceptedFiles => {
        setSign(acceptedFiles[0]);
    }, []);

    const onStampDrop = useCallback(acceptedFiles => {
        setStamp(acceptedFiles[0]);
    }, []);

    const { getRootProps: getSignRootProps, getInputProps: getSignInputProps } = useDropzone({ onDrop: onSignDrop });
    const { getRootProps: getStampRootProps, getInputProps: getStampInputProps } = useDropzone({ onDrop: onStampDrop });

    useEffect(() => {
        dispatch(professionalActions.setProfessionalSelected({}));
        setDoctorId(paramId);
        setMode(pathname.split('/')[2]);
    }, [paramId, pathname, setDoctorId, setMode]);

    useEffect(() => {
        setProfile({ ...DEFAULT_PROFILE });
        clearErrors();
        dispatch(professionalActions.setProfessionalRegistrationErrorModal(false));
        dispatch(professionalActions.setProfessionalRegistrationError({}));
        if (!pathname.includes('crear')) {
            dispatch(professionalActions.getProfessional(paramId));
        }
    }, [
        paramId,
        pathname
    ]);

    useEffect(() => {
        if (isEmpty(selectedProfile)) return;
        const profileData = {
            ...selectedProfile,
            ...selectedProfile?.user
        };
        const parsedProfile = {
            ...profileData,
            admin_locked_actions: profileData?.locked_actions?.filter(({ by_admin }) => !!by_admin),
            birth_date: profileData?.birth_date ? dateToObject(moment(profileData?.birth_date, 'YYYY-MM-DD')) : null,
            city_id: profileData?.city?.id ?? null,
            companies: parseSelect(profileData?.companies ?? []),
            coverages: parseSelect(profileData?.coverages ?? []),
            district_id: profileData?.district?.id ?? null,
            locked_actions: profileData?.locked_actions?.filter(({ by_admin }) => !by_admin),
            province_id: profileData?.province?.id ?? null,
            workplaces: parseSelect(profileData?.workplaces ?? [])

        };
        setProfile(parsedProfile);
    }, [mode, selectedProfile]);

    const handleClear = key => {
        if (!!registrationError[key]) {
            const newErrors = { ...registrationError };
            delete newErrors[key];
            dispatch(professionalActions.setProfessionalRegistrationError(newErrors));
        }
    };

    const handleAddSpeciality = () => {
        if (!!selectedSpecialty) {
            clearStateError('specialties');
            setProfileValue('specialties', [...profile?.specialties, selectedSpecialty]);
            setSelectedSpecialty(null);
        }
    };

    const handleAddCompany = () => {
        if (!!selectedCompany) {
            setProfileValue('companies', [...profile?.companies, selectedCompany]);
            setSelectedCompany(null);
        }
    };
    
    const handleAddCoverage = () => {
        if (!!selectedCoverage) {
            setProfileValue('coverages', [...profile?.coverages, selectedCoverage]);
            setSelectedCoverage(null);
        }
    };

    const handleAddEnrrolment = () => {
        clearError('enrollments');
        setProfileValue('enrollments', [...profile?.enrollments, clearObject(selectedEnrollment)]);
        setSelectedEnrollment({});
    };

    const handleAddPolyOffice = () => {
        if (!!selectedPolyOffice) {
            setProfileValue('polyoffices', [...profile?.polyoffices, selectedPolyOffice]);
            setSelectedPolyOffice(null);
        }
    };

    const handleAddWorkplace = () => {
        if (!!selectedWorkplace) {
            setProfileValue('workplaces', [...profile?.workplaces, selectedWorkplace]);
            setSelectedWorkplace(null);
        }
    };

    const handleAdminPermission = value => () => {
        const { admin_locked_actions } = profile;
        setProfileValue('admin_locked_actions', admin_locked_actions.find(({ name }) => name === value)
            ? admin_locked_actions.filter(({ name }) => name !== value)
            : [...admin_locked_actions, { name: value }]);
    };
    
    const handleAvatarClick = () => {
        if (viewMode) return;
        avatar.current.click();
    };

    const handleChangeProfile = (id, value) => {
        clearError(id);
        if (id === 'identification' || id === 'email') {
            handleClear(id);
        }
        if (id === 'province_id') {
            setProfileValue('city_id', null);
            setProfileValue('district_id', null);
        }
        if (id === 'city_id') {
            setProfileValue('district_id', null);
        }
        setProfileValue(id, value);
    };

    const handleCheckbox = key => () => setProfile(prevProfile => ({ ...prevProfile, [key]: !!prevProfile[key] ? 0 : 1 }));

    const handleImageSelect = () => {
        try {
            const element = avatar.current;
            const FR = new FileReader();
            FR.addEventListener('load', ({ target: { result } }) => {
                setProfileValue('avatar', result);
                element.value = '';
            });
            FR.readAsDataURL(element.files[0]);
            setFile(element.files[0]);
        } catch (err) {

        }
    };

    const handleNotification = () => dispatch(professionalActions.sendProfessionalNotification({ doctor_id: doctorId }));

    const handleOpenModal = () => setModalOpen(true);

    const handlePermission = value => () => {
        const { locked_actions } = profile;
        setProfileValue('locked_actions', locked_actions.find(({ name }) => name === value)
            ? locked_actions.filter(({ name }) => name !== value)
            : [...locked_actions, { name: value }]);
    };

    const handleRemoveCompany = ({ value }) => {
        setProfileValue('companies', profile?.companies.filter(({ id }) => id !== value));
    };

    const handleRemoveCoverage = ({ value }) => {
        setProfileValue('coverages', profile?.coverages.filter(({ id }) => id !== value));
    };

    const handleRemoveEnrollment = index => {
        const items = profile.enrollments.toSpliced(index, 1);
        setProfileValue('enrollments', items);
    };

    const handleRemoveWorkplace = ({ value }) => {
        setProfileValue('workplaces', profile?.workplaces.filter(({ id }) => id !== value));
    };

    const handleRemovePolyOffice = ({ value }) => {
        setProfileValue('polyoffices', profile?.polyoffices.filter(id => id !== value));
    };

    const handleRemoveSpeciality = ({ value }) => {
        setProfileValue('specialties', profile?.specialties.filter(({ id }) => id !== value));
    };

    const handleSubmit = () => {
        if (!validate()) {
            dispatch(professionalActions.setProfessionalRegistrationErrorModal(true));
            return;
        }

        const updatedProfile = {
            ...profile,
            admin_locked_actions: (profile.admin_locked_actions ?? []).map(({ name }) => name),
            appointment_rate: profile.appointment_rate ? profile.appointment_rate : null,
            birth_date: profile.birth_date ? objectToDate(profile.birth_date).format('YYYY-MM-DD') : null,
            call_rate: profile.call_rate ? profile.call_rate : null,
            city_id: profile.city_id ? profile.city_id : 0,
            companies: (profile.companies ?? []).map(({ id }) => id),
            coverages: (profile.coverages ?? []).map(({ id }) => id),
            first_call_rate: profile.first_call_rate ? profile.first_call_rate  : null,
            ...(ssoLogin && { identification: undefined, identification_id: undefined }),
            languages: (profile.languages ?? []).map(({ id }) => id),
            locked_actions: (profile.locked_actions ?? []).map(({ name }) => name),
            offices: (profile.offices ?? []).map(({ id }) => id),
            polyoffices: (profile.polyoffices ?? []).map(({ id }) => id),
            province_id: profile.province_id ? profile.province_id : 0,
            specialties: (profile.specialties ?? []).map(({ id }) => id),
            workplaces: (profile.workplaces ?? []).map(({ id }) => id)
        };
        delete updatedProfile.user;

        const professionalData = {
            data: clearObject({ ...updatedProfile }),
            doctorId,
            file,
            mode,
            sign_image: sign,
            stamp_image: stamp
        };
        dispatch(professionalActions.saveProfessional(professionalData));
    };

    const modalResponse = value => {
        if (value === 'confirm') {
            dispatch(professionalActions.deleteProfessional(doctorId));
        }
        setModalOpen(false);
    };

    const validate = () => {
        const { errors, isValid } = validationSchema({
            birth_date: [!!profile.birth_date, t('Requerido')],
            cellphone_number: [!!profile.cellphone_number, t('Requerido')],
            email: [
                [!!profile.email, t('Requerido')],
                [validations.isValidMail(profile.email), t('Mail inválido')]
            ],
            enrollments: [!isEmpty(profile.enrollments), t('Requerido')],
            first_name: [!!profile.first_name, t('Requerido')],
            gender: [!!profile.gender, t('Requerido')],
            identification: [
                [!!profile.identification, t('Requerido')],
                [!patientIdentificationAlpha || validations.hasOnlyNumbers(profile.identification), t('Solo números')],
                [!identificationFreeLength || profile.identification.length >= 7, t('Mínimo 7 caracteres')],
                [!identificationFreeLength || profile.identification.length <= 8, t('Máximo 8 caracteres')]
            ],
            identification_id: [!!profile?.identification_id, t('Requerido')],
            last_name: [!!profile?.last_name, t('Requerido')],
            password: [
                [editMode || (profile.password && profile.confirm_password), t('Requerido')],
                [!profile?.password || profile?.password === profile?.confirm_password, t('Las contraseñas deben coincidir')],
                [!strongPasswordRequired || (!viewMode && !profile.password) || checkPasswordSecurity(profile?.password), t('La contraseña no es segura')]
            ],
            specialties: [!isEmpty(profile.specialties), t('Requerido')]
        });
        setStateErrors(errors);
        return isValid;
    };

    const title = viewMode ? t('Vista profesional') : editMode ? t('Editar Profesional') : t('Añadir Profesional');
    const hasAdminPermission = permission => !profile?.admin_locked_actions?.map(action => action.name).includes(permission);
    const hasPermission = permission => !profile?.locked_actions?.map(action => action.name).includes(permission);
    if (loadingProfessional) {
        return (
            <FormLayout goBackTo={ from ?? t('/profesionales/listar') } title={ title }>
                <Loader active={ true } />
            </FormLayout>
        );
    }

    return (
        <>
            <ErrorModal
                active={ registrationErrorModal }
                text={ t('Por favor, complete correctamente todos los campos') }
                onClick={ () => dispatch(professionalActions.setProfessionalRegistrationErrorModal(false)) }
            />
            <FormLayout goBackTo={ from ?? t('/profesionales/listar') } title={ title }>
                <Col xs="12">
                    <ProfileContainer>
                        <ProfileIcon onClick={ handleAvatarClick }>
                            {
                                !!profile?.avatar ?
                                    <Avatar src={ profile?.avatar?.url ?? profile.avatar }  /> :
                                    <MaterialIcon
                                        color="#FFFFFF"
                                        icon="face"
                                        size={ 70 }
                                    />
                            }
                        </ProfileIcon>
                        <ProfileText onClick={ handleAvatarClick }>
                            { t('Subir foto de perfil') }
                        </ProfileText>
                    </ProfileContainer>
                    <Row>
                        <Col md="6" sm="12">
                            <FormGroup label={ t('Nombre') }>
                                <Input
                                    disabled={ viewMode }
                                    error={ stateErrors.first_name }
                                    value={ profile.first_name }
                                    onChange={ value => handleChangeProfile('first_name', value) }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6" sm="12">
                            <FormGroup label={ t('Apellido') }>
                                <Input
                                    disabled={ viewMode }
                                    error={ stateErrors.last_name }
                                    value={ profile.last_name }
                                    onChange={ value => handleChangeProfile('last_name', value) }
                                />
                            </FormGroup>
                        </Col>
                        {
                            !ssoLogin &&
                            <>
                                <Col sm="2" xs="12">
                                    <FormGroup label={ t('Tipo') } marginBottom="20px">
                                        <SelectSearch
                                            disabled={ viewMode }
                                            hasError={ !!stateErrors.identification_id }
                                            options={ documentTypeOptions }
                                            placeholder={ t('DNI') }
                                            value={ 1 }
                                            width="100%"
                                            onChange={ value => handleChangeProfile('identification_id', value) }
                                        />
                                    </FormGroup>
                                </Col>
                                <Col >
                                    <FormGroup label={ t('Documento') }>
                                        <Input
                                            disabled={ viewMode }
                                            error={ stateErrors.identification ?? registrationError.identification }
                                            maxLength={ identificationFreeLength ? undefined : 8 }
                                            type={ patientIdentificationAlpha ? 'text' : 'number' }
                                            value={ profile.identification }
                                            onChange={ value => handleChangeProfile('identification', value) }
                                        />
                                    </FormGroup>
                                </Col>
                            </>
                        }
                        {
                            !!profile?.cuit &&
                            <Col md="6" xs="12">
                                <FormGroup label={ t('CUIT') } marginBottom="20px">
                                    <Container margin="15px 0px 0px 0px">
                                        <Text>{ profile.cuit} </Text>
                                    </Container>
                                </FormGroup>
                            </Col>
                        }
                        <Col md="6" xs="12">
                            <FormGroup label={ t('Fecha de Nacimiento') } marginBottom="20px">
                                <DateSelect
                                    disabled={ viewMode }
                                    error={ stateErrors?.birth_date ?? '' }
                                    value={ profile.birth_date }
                                    onChange={ value => handleChangeProfile('birth_date', value) }
                                />
                            </FormGroup>
                        </Col>
                        <Col sm="6">
                            <FormGroup label={ t('Teléfono') }>
                                <PhoneInput
                                    disabled={ viewMode }
                                    margin="0px 0px 20px 0px"
                                    required={ true }
                                    value={ profile.cellphone_number }
                                    onChange={ value => handleChangeProfile('cellphone_number', value) }
                                />
                                <ErrorMessage relative error={ stateErrors.cellphone_number } marginLeft={ 0 } />
                            </FormGroup>
                        </Col>
                        <Col md="6" sm="12">
                            <FormGroup label={ t('Sexo') } marginBottom="20px">
                                <GenderPicker
                                    disabled={ viewMode }
                                    value={ profile.gender }
                                    onChange={ value => handleChangeProfile('gender', value) }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup label={ t('Email') }>
                                <Input
                                    disabled={ viewMode }
                                    error={ stateErrors.email ?? registrationError.email }
                                    type="email"
                                    value={ profile.email }
                                    onChange={ value => handleChangeProfile('email', value) }
                                />
                            </FormGroup>
                        </Col>
                        {
                            professionalLocation &&
                            <>
                                <Col md="6" sm="12">
                                    <FormGroup label={ t('Provincia') }>
                                        <ProvincePicker
                                            disabled={ viewMode }
                                            margin="0px 0px 20px 0px"
                                            value={ profile?.province_id }
                                            onChange={ value => handleChangeProfile('province_id', value) }
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6" sm="12">
                                    <FormGroup label={ t('Ciudad') }>
                                        <CityPicker
                                            disabled={ viewMode }
                                            margin="0px 0px 20px 0px"
                                            params={ profile?.province_id }
                                            value={ profile?.city_id }
                                            onChange={ value => handleChangeProfile('city_id', value) }
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6" sm="12">
                                    <FormGroup label={ t('Barrio') }>
                                        <DistrictPicker
                                            disabled={ viewMode }
                                            margin="0px 0px 20px 0px"
                                            params={ profile?.city_id }
                                            value={ profile?.district_id }
                                            onChange={ value => handleChangeProfile('district_id', value) }
                                        />
                                    </FormGroup>
                                </Col>
                                {
                                    !!profile?.public_url &&
                                    <Col md="6" xs="12">
                                        <FormGroup label={ t('URL pública') } marginBottom="20px">
                                            <Container margin="15px 0px 0px 0px">
                                                <Text>{ profile.public_url} </Text>
                                            </Container>
                                        </FormGroup>
                                    </Col>
                                }
                            </>
                        }
                    </Row>
                    <Row>
                        <Col md="4" sm="12">
                            <FormGroup label={ t('Tipo de matrícula') } marginBottom="20px">
                                <EnrollmentTypePicker
                                    changeUnique
                                    objectChange
                                    disabled={ viewMode }
                                    hasError={ !!stateErrors.enrollments }
                                    value={ selectedEnrollment?.enrollment_id }
                                    onChange={ value => setSelectedEnrollmentValues({ enrollment_id: value?.id, name: value?.name }) }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="2" sm="12">
                            <FormGroup label="N°">
                                <Input
                                    disabled={ viewMode }
                                    type="number"
                                    value={ selectedEnrollment.number ?? '' }
                                    width="100%"
                                    onChange={ value => setSelectedEnrollmentValue('number', value) }
                                />
                            </FormGroup>
                        </Col>
                        {
                            enrollmentSpecialty &&
                            <Col md="4" sm="12">
                                <FormGroup label={ t('Especialidad') }>
                                    <SpecialtyPicker
                                        objectChange
                                        disabled={ viewMode }
                                        margin="0px 0px 10px 0px"
                                        value={ selectedEnrollment?.specialty_id }
                                        onChange={ value => setSelectedEnrollmentValues({ specialty: value?.name, specialty_id: value?.id }) }
                                    />
                                </FormGroup>
                            </Col>
                        }
                        {
                            showEnrollmentIntegration &&
                            <>
                                <Col md="4" sm="12" xs="12">
                                    <FormGroup label={ t('Categoría') }>
                                        <Input
                                            value={ selectedEnrollment?.category ?? ''  }
                                            onChange={ value => setSelectedEnrollmentValue('category', value) }
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4" sm="12" xs="12">
                                    <FormGroup label={ t('Código de entidad') }>
                                        <Input
                                            value={ selectedEnrollment?.entity ?? ''  }
                                            onChange={ value => setSelectedEnrollmentValue('entity', value) }
                                        />
                                    </FormGroup>
                                </Col>
                            </>
                        }
                        <ResponsiveCol breakpoint="1053px">
                            {
                                !viewMode &&
                                <AddButton
                                    disabled={ !selectedEnrollment?.enrollment_id || !selectedEnrollment?.number }
                                    onClick={ handleAddEnrrolment }
                                />
                            }
                        </ResponsiveCol>
                        <ErrorMessage relative error={ stateErrors?.enrollments } margin="0px 0px 20px 0px" />
                        {
                            !isEmpty(profile.enrollments) &&
                            <Col md="12">
                                <TagList
                                    items={ profile.enrollments.map(item => {
                                        const { category, entity, name, number, specialty } = item;
                                        return ({
                                            ...item,
                                            label: `${ name } ${ number }${ specialty ? ` - ${ specialty }` : '' }${ category || entity ? ` <${ category ?? '' }${ entity ?? '' }>` : '' }`
                                        });
                                    }) }
                                    margin="0px 0px 10px 0px"
                                    readOnly={ viewMode }
                                    onRemove={ (v, index) => handleRemoveEnrollment(index) }
                                />
                            </Col>
                        }
                    </Row>
                    <Row>
                        <Col md="6" sm="12">
                            <FormGroup label={ t('Especialidad') }>
                                <SpecialtyPicker
                                    objectChange
                                    disabled={ viewMode }
                                    filter={ ({ value }) => !profile?.specialties?.find(({ id }) => id === value) }
                                    hasError={ !!stateErrors.specialties }
                                    margin="0px 0px 10px 0px"
                                    value={ selectedSpecialty?.id }
                                    onChange={ setSelectedSpecialty }
                                />
                                <ErrorMessage relative error={ stateErrors?.specialties } margin="0px 0px 20px 0px" />
                            </FormGroup>
                        </Col>
                        <ResponsiveCol>
                            {
                                !viewMode &&
                                <AddButton
                                    disabled={ !selectedSpecialty }
                                    onClick={ handleAddSpeciality }
                                />
                            }
                        </ResponsiveCol>
                        {
                            !isEmpty(profile?.specialties) &&
                            <Col xs="12">
                                <TagList
                                    items={ parseSelect(profile?.specialties) }
                                    margin="0px 0px 20px 0px"
                                    readOnly={ viewMode }
                                    onRemove={ value => handleRemoveSpeciality(value) }
                                />
                            </Col>
                        }
                    </Row>
                    {
                        companyConfig &&
                        <Row>
                            <Col xs="12">
                                <Checkbox
                                    checked={ !!profile.all_companies }
                                    disabled={ viewMode }
                                    label={ t('Asociar todas las empresas') }
                                    margin="0px 0px 10px 0px"
                                    onClick={ handleCheckbox('all_companies') }
                                />
                            </Col>
                            {
                                !profile.all_companies &&
                                <>
                                    <Col md="6" sm="12">
                                        <FormGroup label={ t('Empresas') }>
                                            <CompanyPicker
                                                objectChange
                                                disabled={ viewMode }
                                                filter={ ({ value }) => !profile?.companies?.find(({ id }) => id === value) }
                                                hasError={ !!stateErrors.companies }
                                                margin="0px 0px 10px 0px"
                                                value={ selectedCompany?.id }
                                                onChange={ setSelectedCompany }
                                            />
                                            <ErrorMessage relative error={ stateErrors?.companies } margin="0px 0px 20px 0px" />
                                        </FormGroup>
                                    </Col>
                                    <ResponsiveCol>
                                        {
                                            !viewMode &&
                                            <AddButton
                                                disabled={ !selectedCompany }
                                                onClick={ handleAddCompany }
                                            />
                                        }
                                    </ResponsiveCol>
                                    {
                                        !isEmpty(profile?.companies) &&
                                        <Col xs="12">
                                            <TagList
                                                items={ parseSelect(profile?.companies) }
                                                margin="0px 0px 20px 0px"
                                                readOnly={ viewMode }
                                                onRemove={ value => handleRemoveCompany(value) }
                                            />
                                        </Col>
                                    }
                                </>
                            }
                        </Row>
                    }
                    {
                        workplacesConfig &&
                        <Row>
                            <Col xs="12">
                                <Checkbox
                                    checked={ !!profile.all_workplaces }
                                    disabled={ viewMode }
                                    label={ t('Asociar todos los lugares de trabajo') }
                                    margin="0px 0px 10px 0px"
                                    onClick={ handleCheckbox('all_workplaces') }
                                />
                            </Col>
                            {
                                !profile?.all_workplaces &&
                                <>
                                    <Col md="6" sm="12">
                                        <FormGroup label={ t('Lugares de trabajo') }>
                                            <WorkplacePicker
                                                objectChange
                                                disabled={ viewMode }
                                                filter={ ({ value }) => !profile?.workplaces?.find(({ id }) => id === value) }
                                                hasError={ !!stateErrors.workplaces }
                                                margin="0px 0px 10px 0px"
                                                value={ selectedWorkplace?.id }
                                                onChange={ setSelectedWorkplace }
                                            />
                                            <ErrorMessage relative error={ stateErrors?.workplaces } margin="0px 0px 20px 0px" />
                                        </FormGroup>
                                    </Col>
                                    <ResponsiveCol>
                                        {
                                            !viewMode &&
                                            <AddButton
                                                disabled={ !selectedWorkplace }
                                                onClick={ handleAddWorkplace }
                                            />
                                        }
                                    </ResponsiveCol>
                                    {
                                        !isEmpty(profile?.workplaces) &&
                                        <Col xs="12">
                                            <TagList
                                                items={ parseSelect(profile?.workplaces) }
                                                margin="0px 0px 20px 0px"
                                                readOnly={ viewMode }
                                                onRemove={ value => handleRemoveWorkplace(value) }
                                            />
                                        </Col>
                                    }
                                </>
                            }
                        </Row>
                    }
                    {
                        professionalCoverageSelection &&
                        <Row>
                            <Col xs="12">
                                <Checkbox
                                    checked={ !!profile.all_coverages }
                                    disabled={ viewMode }
                                    label={ t('Permitir atender por cualquier cobertura') }
                                    margin="0px 0px 10px 0px"
                                    onClick={ handleCheckbox('all_coverages') }
                                />
                            </Col>
                            {
                                !profile.all_coverages &&
                                <>
                                    <Col md="8" sm="12">
                                        <FormGroup label={ t('Coberturas') }>
                                            <CoveragePicker
                                                objectChange
                                                disabled={ viewMode }
                                                filter={ (({ value }) => !profile?.coverages?.find(({ id }) => id === value)) }
                                                margin="0px 0px 10px 0px"
                                                placeholder={ t('Seleccione una cobertura') }
                                                value={ selectedCoverage?.id }
                                                onChange={ setSelectedCoverage }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <ResponsiveCol md="4" sm="12">
                                        {
                                            !viewMode &&
                                            <AddButton
                                                disabled={ !selectedCoverage }
                                                onClick={ handleAddCoverage }
                                            />
                                        }
                                    </ResponsiveCol>
                                    {
                                        !isEmpty(profile?.coverages) &&
                                        <Col>
                                            <TagList
                                                items={ profile?.coverages }
                                                margin="0px 0px 20px 0px"
                                                readOnly={ viewMode }
                                                onRemove={ value => handleRemoveCoverage(value) }
                                            />
                                        </Col>
                                    }
                                </>
                            }
                        </Row>
                    }
                    {
                        polyOfficesConfig &&
                        <Row>
                            <Col md="6" sm="12">
                                <FormGroup label={ t('Policonsultorios') }>
                                    <PolyOfficePicker
                                        objectChange
                                        disabled={ viewMode }
                                        filter={ ({ value }) => !profile?.polyoffices?.find(({ value: searchValue }) => searchValue === value) }
                                        margin="0px 0px 10px 0px"
                                        value={ selectedPolyOffice?.value }
                                        onChange={ setSelectedPolyOffice }
                                    />
                                </FormGroup>
                            </Col>
                            <ResponsiveCol>
                                {
                                    !viewMode &&
                                    <AddButton
                                        disabled={ !selectedPolyOffice }
                                        onClick={ handleAddPolyOffice }
                                    />
                                }
                            </ResponsiveCol>
                            {
                                !isEmpty(profile?.polyoffices) &&
                                <Col xs="12">
                                    <TagList
                                        items={ parseSelect(profile?.polyoffices) }
                                        margin="0px 0px 20px 0px"
                                        readOnly={ viewMode }
                                        onRemove={ handleRemovePolyOffice }
                                    />
                                </Col>
                            }
                        </Row>
                    }
                    <Row>
                        {
                            turns && allowChangeRateFirst  &&
                            <Col md="4" xs="12">
                                <FormGroup label={ t('Tarifa de la primera consulta') }>
                                    <Input
                                        disabled={ viewMode }
                                        type="number"
                                        value={ profile.first_call_rate ? profile.first_call_rate.toString() : '' }
                                        onChange={ value => handleChangeProfile('first_call_rate', value) }
                                    />
                                </FormGroup>
                            </Col>
                        }
                        {
                            turns &&
                            <Col md="4" xs="12">
                                <FormGroup label={ t('Tarifa de la consulta') }>
                                    <Input
                                        disabled={ viewMode }
                                        type="number"
                                        value={ profile.appointment_rate ? profile.appointment_rate.toString() : '' }
                                        onChange={ value => handleChangeProfile('appointment_rate', value) }
                                    />
                                </FormGroup>
                            </Col>
                        }
                        {
                            professionalCallRate &&
                            <Col sm="4" xs="12">
                                <FormGroup label={ t('Tarifa directa') }>
                                    <Input
                                        disabled={ viewMode }
                                        type="number"
                                        value={ profile.call_rate ?? '' }
                                        onChange={ value => handleChangeProfile('call_rate', value) }
                                    />
                                </FormGroup>
                            </Col>
                        }
                    </Row>
                    <Row>
                        <Col>
                            <FormGroup label={ t('Referencia Externa') }>
                                <Input
                                    disabled={ viewMode }
                                    placeholder={ `(${ t('Opcional') })` }
                                    value={ profile.external_reference ?? '' }
                                    onChange={ value => handleChangeProfile('external_reference', value) }
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    {
                        professionalBio &&
                        <Row>
                            <Col xs="12">
                                <FormGroup label={ t('Extracto') }>
                                    <Input
                                        disabled={ viewMode }
                                        fontSize="14px"
                                        height="200px"
                                        placeholder={ t('Escriba una breve reseña del profesional') }
                                        type="area"
                                        value={ profile.bio ?? '' }
                                        onChange={ value => handleChangeProfile('bio', value) }
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    }
                    {
                        professionalAllowExternal &&
                        <Row>
                            <Col md="9" sm="12">
                                <FormGroup label={ t('Tipo de profesional') } marginBottom="20px">
                                    <Checkbox
                                        checked={ externalService ? !!profile.external_service : !!profile.external }
                                        disabled={ viewMode }
                                        label="Externo"
                                        onClick={
                                            externalService
                                                ? handleCheckbox('external_service', profile.external_service)
                                                : handleCheckbox('external', profile.external)
                                        }
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    }
                    {
                        professionalLockedActions &&
                        <Row>
                            <Col md="9" sm="12">
                                <FormGroup label={ t('Acciones permitidas') } margin="0px 0px 10px 0px">
                                    <Row>
                                        {
                                            adminLockedActions.map(({
                                                key,
                                                labelAction
                                            }) =>
                                                <Col key={ key } xs="12">
                                                    <Checkbox
                                                        checked={ !!hasAdminPermission(key) }
                                                        disabled={ viewMode }
                                                        label={ labelAction }
                                                        margin="0px 0px 5px 0px"
                                                        onClick={ handleAdminPermission(key) }
                                                    />
                                                </Col>
                                            )
                                        }
                                    </Row>
                                </FormGroup>
                            </Col>
                        </Row>
                    }
                    {
                        !!doctorsRelated &&
                        <Row>
                            <Col xs="12">
                                <FormGroup label={ t('Configuración') } margin="0px 0px 10px 0px">
                                    <Row>
                                        <Col xs="12">
                                            <Checkbox
                                                checked={ !!profile.show_patients }
                                                disabled={ viewMode }
                                                label={ t('Ver todos los pacientes') }
                                                margin="0px 0px 5px 0px"
                                                onClick={ handleCheckbox('show_patients', profile.show_patients) }
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>
                            </Col>
                        </Row>
                    }
                    <Row>
                        <Col md="9" sm="12">
                            <FormGroup label={ t('Acciones configuradas') } margin="0px 0px 10px 0px">
                                <Row>
                                    {
                                        configActions.map(({
                                            isLockedAction = true,
                                            labelAction,
                                            key
                                        }) =>
                                            <Col key={ key } md="10" sm="12">
                                                <Checkbox
                                                    checked={ isLockedAction
                                                        ? !!hasPermission(key)
                                                        : !!profile[key]
                                                    }
                                                    disabled={ viewMode }
                                                    label={ labelAction }
                                                    margin="0px 0px 5px 0px"
                                                    onClick={ isLockedAction
                                                        ? handlePermission(key)
                                                        : handleCheckbox(key, profile[key])
                                                    }
                                                />
                                            </Col>
                                        )
                                    }
                                </Row>
                            </FormGroup>
                        </Col>
                    </Row>
                    {
                        recipes &&
                        <StyledRow>
                            <Col sm="6" xs="12">
                                {
                                    !viewMode &&
                                    <Row>
                                        <Col>
                                            <DropContainer { ...getSignRootProps() }>
                                                <input { ...getSignInputProps() } />
                                                {
                                                    isEmpty(sign)
                                                        ? t('Arrastra el archivo para la firma o haz click')
                                                        : sign.name
                                                }
                                            </DropContainer>
                                        </Col>
                                    </Row>
                                }
                                {
                                    !!profile.sign_image &&
                                    <FormGroup label={ t('Firma actual') } margin="0px 0px 20px 0px">
                                        <ImagePreview src={ profile.sign_image?.url } />
                                    </FormGroup>
                                }
                            </Col>
                            <Col sm="6" xs="12">
                                {
                                    !viewMode &&
                                    <Row>
                                        <Col>
                                            <DropContainer { ...getStampRootProps() }>
                                                <input { ...getStampInputProps() } />
                                                {
                                                    isEmpty(stamp)
                                                        ? t('Arrastra el archivo para el sello o haz click')
                                                        : stamp.name
                                                }
                                            </DropContainer>
                                        </Col>
                                    </Row>
                                }
                                {
                                    !!profile.stamp_image &&
                                    <FormGroup label={ t('Sello actual') } margin="0px 0px 20px 0px">
                                        <ImagePreview src={ profile.stamp_image?.url } />
                                    </FormGroup>
                                }
                            </Col>
                        </StyledRow>
                    }
                    <Row>
                        {
                            !viewMode &&
                            <>
                                <Col md="6" sm="12">
                                    <FormGroup label={ t('Contraseña') } margin="0px 0px 20px 0px">
                                        <Input
                                            autocomplete="off"
                                            disabled={ viewMode }
                                            error={ stateErrors.password }
                                            type="password"
                                            value={ profile.password }
                                            onChange={ value => handleChangeProfile('password', value) }
                                        />
                                    </FormGroup>
                                    { strongPasswordRequired && <PasswordSecurityCheck password={ profile.password } /> }
                                </Col>
                                <Col md="6" sm="12">
                                    <FormGroup label={ t('Confirmar Contraseña') } margin="0px 0px 20px 0px">
                                        <Input
                                            autocomplete="off"
                                            disabled={ viewMode }
                                            error={ stateErrors.password }
                                            type="password"
                                            value={ profile.confirm_password }
                                            onChange={ value => handleChangeProfile('confirm_password', value) }
                                        />
                                    </FormGroup>
                                </Col>
                            </>
                        }
                        <ButtonsContainer xs="12">
                            {
                                editMode &&
                                <ExpandButton
                                    danger
                                    caption={ t('Eliminar') }
                                    size={ 100 }
                                    onClick={ handleOpenModal }
                                />
                            }
                            {
                                ((!viewMode && !editMode) || (editMode)) &&
                                <ExpandButton
                                    caption={ t('Confirmar') }
                                    disabled={ saveLoading }
                                    loading={ saveLoading }
                                    size={ 100 }
                                    onClick={ handleSubmit }
                                />
                            }
                            {
                                viewMode &&
                                <ExpandButton
                                    caption={ t('Notificación') }
                                    size={ 100 }
                                    onClick={ handleNotification }
                                />
                            }
                        </ButtonsContainer>
                    </Row>
                </Col>
                <InputHidden
                    accept="image/*"
                    ref={ avatar }
                    type="file"
                    onChange={ handleImageSelect }
                />
                <InputHidden
                    sharap={ file }
                />
                <ConfirmationModal
                    actionHandler={ modalResponse }
                    active={ modalOpen }
                    title={ t('¿Quieres dar de baja este Profesional?') }
                />
            </FormLayout>
            <ConfirmationModal
                actionHandler={ modalResponse }
                active={ modalOpen }
                title={ t('¿Quieres dar de baja este Profesional?') }
            />
            <NotificationModal
                active={ !!notificationResponse }
                caption={ t('Aceptar') }
                height="300px"
                secondLine={ notificationResponse }
                singleButton={ true }
                title={ t('Resultado del envío') }
                onBack={ () => dispatch(professionalActions.setProfessionalNotificationResponse('')) }
            />
        </>
    );

};

export default ProfessionalRegistration;
